/**
 * Activating a control with a finger that is not the first one down.
 *
 * ── WHY THIS EXISTS ──
 *
 * A `click` on a touch screen is not dispatched from the pointer stream. It comes from a **tap
 * gesture** the platform's recogniser decided happened, and with a thumb already resting on the
 * joystick a second contact is part of a multi-finger gesture rather than a tap — so no click
 * arrives at all, however many times the control is pressed. The player found it twice, on two
 * different controls: first the Say button ("I held joystick with thumb and tried pressing say
 * over and over but nothing until I lift my thumb and then press"), and then, once that was
 * fixed, the rows inside the panel it opens ("if I try to select a saying with my thumb down,
 * that fails until I lift thumb up").
 *
 * The second report is what turned this from a fix into a primitive. It is not a property of the
 * Say button or of a saying row; it is a property of **every control drawn over a live arena**,
 * because the arena is the one screen in this app where a finger is expected to be down on
 * something else the whole time. So the rule is written once and applied to all of them, rather
 * than discovered one control at a time by a player.
 *
 * ── WHAT IT DOES INSTEAD ──
 *
 * The press is taken on **`pointerup` over the control**, which is dispatched per pointer with no
 * gesture recogniser in the way. It still lands at the *end* of the gesture, which is what a
 * control that opens or chooses something wants: `ActionButtonTrigger` has the long version of
 * why an in-arena menu must not fire on the way down, and the short version is that the panel
 * would appear under a finger that is still travelling.
 *
 * `onClick` is kept for the **keyboard alone**. A browser reports `detail === 0` for Enter or
 * Space on a focused control, and that is the one activation no pointer event accompanies;
 * anything with a `detail` above zero is the tail of a pointer gesture whose press has already
 * been taken, and firing on it too would count one press twice.
 *
 * ── WHAT IT DELIBERATELY DOES NOT COVER ──
 *
 * The three game actions — split, eject, fire — take their press on `pointerdown` and always
 * have, because a split has a 20 Hz deadline and waiting for the finger to come back up is a
 * frame of lag a player can feel. They were never affected by any of this, which is exactly the
 * evidence that the diagnosis is right: `pointerdown` is dispatched per pointer, and those
 * controls worked with a thumb down the whole time.
 */

import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useRef } from 'react';

export interface TapActivateHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: () => void;
  onPointerLeave: () => void;
  onClick: (event: ReactMouseEvent) => void;
}

/**
 * Handlers that fire `onActivate` once per tap, from any finger.
 *
 * Spread onto a `<button>`. `disabled` is read at both ends of the gesture, so a control that
 * becomes disabled while a finger is on it fires nothing when that finger lifts.
 */
export function useTapActivate(onActivate: () => void, disabled = false): TapActivateHandlers {
  // Read through refs so the returned handlers keep a stable identity across the renders that
  // flip either of them — a cooldown ticking under a picker re-renders these rows constantly.
  const activateRef = useRef(onActivate);
  activateRef.current = onActivate;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  /**
   * The pointer this gesture began with, so the `pointerup` completing it can be told from one
   * that merely ended over this control — a finger that started somewhere else, or the second
   * half of a gesture whose first half was on the canvas. Null between taps.
   */
  const pointerId = useRef<number | null>(null);

  const onPointerDown = useCallback((event: ReactPointerEvent) => {
    if (disabledRef.current) return;
    pointerId.current = event.pointerId;
  }, []);

  const clear = useCallback(() => {
    pointerId.current = null;
  }, []);

  const onPointerUp = useCallback((event: ReactPointerEvent) => {
    const began = pointerId.current === event.pointerId;
    pointerId.current = null;
    // A gesture that started elsewhere and merely ended here is not a press, and neither is one
    // on a control that has become disabled since it started.
    if (began && !disabledRef.current) activateRef.current();
  }, []);

  const onClick = useCallback((event: ReactMouseEvent) => {
    // The keyboard, and nothing else. See the docblock: a pointer's press has already been taken
    // on `pointerup`, so firing here as well would be that press counted twice.
    if (event.detail === 0 && !disabledRef.current) activateRef.current();
  }, []);

  return { onPointerDown, onPointerUp, onPointerCancel: clear, onPointerLeave: clear, onClick };
}
